import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SERVICE_CATEGORIES } from "../data/content";
import { useBooking } from "./BookingModal";

const Services = () => {
  const [active, setActive] = useState(SERVICE_CATEGORIES[0].id);
  const { openBooking } = useBooking();
  const current = SERVICE_CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative px-6 py-32 md:px-12 md:py-48"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-16 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="chip mb-6">
              <span className="dot" />
              02 · Services
            </div>
            <h2 className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl">
              Everything for
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                the whole you.
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[color:var(--muted)]">
            A complete menu across hair, skin, makeup, nails and body — no
            prices listed here so nothing gets between you and the ritual.
            Message us for a personalised quote.
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[color:var(--border)] pb-6">
          {SERVICE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-testid={`service-tab-${cat.id}`}
              onClick={() => setActive(cat.id)}
              className={`group relative border px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-all ${
                active === cat.id
                  ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                  : "border-[color:var(--border)] text-[color:var(--muted)] hover:border-[color:var(--primary)] hover:text-[color:var(--text)]"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
            className="mt-16"
          >
            <div className="mb-12 flex items-baseline gap-6">
              <span className="font-serif text-6xl italic-serif text-[color:var(--primary)] md:text-8xl">
                {current.label}
              </span>
              <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)] md:inline">
                — {current.tagline}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              {current.groups.map((g, i) => (
                <ServiceGroup key={g.title + i} group={g} idx={i} />
              ))}
            </div>

            <div className="mt-16 flex flex-col items-start gap-6 border-t border-[color:var(--border)] pt-10 md:flex-row md:items-center md:justify-between">
              <p className="max-w-md text-sm text-[color:var(--muted)]">
                Something else in mind? We do custom. Message us on Instagram
                or WhatsApp and we&rsquo;ll build the appointment around you.
              </p>
              <button
                data-testid="services-book-btn"
                onClick={openBooking}
                className="group inline-flex items-center gap-3 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] transition hover:bg-[color:var(--primary)]"
              >
                Book this menu →
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const ServiceGroup = ({ group, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: idx * 0.05 }}
      className="border border-[color:var(--border)] bg-[color:var(--surface)]"
    >
      <button
        data-testid={`group-toggle-${group.title.replace(/\s+/g, "-").toLowerCase()}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left md:px-8"
      >
        <span className="font-serif text-2xl leading-tight md:text-3xl">
          {group.title}
        </span>
        <span
          className={`flex h-9 w-9 items-center justify-center border border-[color:var(--border)] transition-transform duration-500 ${
            open
              ? "rotate-45 border-[color:var(--primary)] text-[color:var(--primary)]"
              : ""
          }`}
        >
          <Plus size={14} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 md:px-8 md:pb-8">
              <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                {group.items.map((item, i) => (
                  <div
                    key={item + i}
                    className="flex items-baseline justify-between gap-4 border-b border-dashed border-[color:var(--border)] py-3"
                  >
                    <span className="text-sm text-[color:var(--text)]">
                      {item}
                    </span>
                    <span className="font-mono text-[10px] text-[color:var(--muted)]">
                      · {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Services;
